import { useAppTranslation } from '../i18n/useAppTranslation';
import React, { useId, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

const metrics: Record<string, { label: string; color: string }> = {
  global_score: { label: "Score global", color: "#D9B76A" },
  physical_condition: { label: "Endurance", color: "#62B5E5" },
  endurance: { label: "Endurance", color: "#62B5E5" },
  technical_mastery: { label: "Technique", color: "#A99AEF" },
  mental_strength: { label: "Mental", color: "#79C6AE" },
  motivation: { label: "Motivation", color: "#D9B76A" },
  coordination: { label: "Coordination", color: "#62B5E5" },
  stress_level: { label: "Gestion des émotions", color: "#A99AEF" },
  fatigue_level: { label: "Niveau de forme", color: "#79C6AE" },
  explosivite: { label: "Explosivité", color: "#EB9AA8" },
  tactique: { label: "Tactique", color: "#E6AB72" },
  volontaire: { label: "Volonté", color: "#94B9F2" },
};
export const scoreText = (value: unknown, locale = "fr-FR") =>
  typeof value === "number" && Number.isFinite(value)
    ? value.toLocaleString(locale, { maximumFractionDigits: 1 }) + " / 10"
    : "Non renseigné";
const formatDateText = (date: string, pattern = "dd MMM yyyy", dateLocale = fr) => {
  try {
    return format(parseISO(date), pattern, { locale: dateLocale });
  } catch {
    return "Date inconnue";
  }
};
const panel = {
  p: { xs: 2, sm: 3 },
  borderRadius: 3,
  height: "100%",
  minWidth: 0,
};

export function PerformanceProfile({
  data,
  date,
}: {
  data: { subject: string; A: number | null }[];
  date?: string;
}) {
  const { tr, locale, dateLocale } = useAppTranslation();
  const dateText = (date: string, pattern?: string) => tr(formatDateText(date, pattern, dateLocale));
  const id = useId();
  return (
    <Paper
      component="section"
      aria-labelledby={id}
      variant="outlined"
      sx={panel}
    >
      <Typography id={id} variant="h6" fontWeight={700}>
         {tr("Profil de la dernière séance")} </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {date ? dateText(date) + " · " : ""}{tr("Chaque critère est noté sur 10.")} </Typography>
      {data.length ? (
        <Stack
          spacing={1.5}
          component="ul"
          sx={{ listStyle: "none", p: 0, m: 0 }}
        >
          {data.map((item) => (
            <Box component="li" key={item.subject}>
              <Stack
                direction="row"
                justifyContent="space-between"
                gap={1}
                sx={{ mb: 0.5 }}
              >
                <Typography variant="body2">
                  {item.subject === "En forme" || item.subject === "Fatigue"
                    ? tr("Niveau de forme")
                    : item.subject === "Gestion Émotion"
                    ? tr("Gestion des émotions")
                    : item.subject}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: "nowrap",
                  }}
                >
                  {tr(scoreText(item.A, locale))}
                </Typography>
              </Stack>
              <Box
                aria-hidden="true"
                sx={{
                  height: 6,
                  borderRadius: 4,
                  bgcolor: "action.hover",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    borderRadius: 4,
                    bgcolor: "primary.main",
                    width: Math.max(0, Math.min(100, (item.A ?? 0) * 10)) + "%",
                  }}
                />
              </Box>
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography color="text.secondary">
           {tr("Les critères apparaîtront après la première évaluation.")} </Typography>
      )}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 2 }}
      >
         {tr("Une barre plus longue indique une note plus élevée. Ces évaluations sont propres à votre suivi.")} </Typography>
    </Paper>
  );
}

export function PerformanceEvolution({
  data,
  series,
  wellbeing = false,
}: {
  data: Record<string, any>[];
  series: string[];
  wellbeing?: boolean;
}) {
  const { tr, locale, dateLocale } = useAppTranslation();
  const dateText = (date: string, pattern?: string) => tr(formatDateText(date, pattern, dateLocale));
  const theme = useTheme();
  const id = useId();
  const available = series.filter((key) => metrics[key]);
  const [selected, setSelected] = useState(() =>
    available.includes("global_score")
      ? [
          "global_score",
          ...available.filter((k) => k !== "global_score").slice(0, 3),
        ]
      : available.slice(0, 3)
  );
  const [table, setTable] = useState(false);
  const active = available.filter((key) => selected.includes(key));
  const rows = [...data].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  const first = rows[0],
    last = rows[rows.length - 1];
  const delta =
    rows.length > 1 &&
    typeof first.global_score === "number" &&
    typeof last.global_score === "number"
      ? last.global_score - first.global_score
      : null;
  return (
    <Paper
      component="section"
      aria-labelledby={id}
      variant="outlined"
      sx={panel}
    >
      <Typography id={id} variant="h6" fontWeight={700}>
        {wellbeing
          ? tr("Évolution des critères personnels")
          : tr("Progression au fil des séances")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {tr("{count} séances évaluées", { count: rows.length })}
        {first
          ? " · " +
            dateText(first.date) +
            (rows.length > 1 ? " – " + dateText(last.date) : "")
          : ""}
      </Typography>
      {!wellbeing && last && (
        <Stack
          direction="row"
          gap={1}
          alignItems="baseline"
          flexWrap="wrap"
          sx={{ my: 2 }}
        >
          <Typography variant="h4" fontWeight={700}>
            {tr(scoreText(last.global_score, locale))}
          </Typography>
          <Typography variant="caption" color="text.secondary">
             {tr("Dernier score global")} </Typography>
          {delta !== null && (
            <Chip
              size="small"
              variant="outlined"
              label={
                tr("{score} pt depuis la première séance affichée", { score: (delta > 0 ? "+" : "") + delta.toLocaleString(locale, { maximumFractionDigits: 1 }) })
              }
            />
          )}
        </Stack>
      )}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 2, mb: 1 }}
      >
         {tr("Choisissez les courbes à afficher. Toutes les notes utilisent la même échelle, de 0 à 10.")} </Typography>
      <Stack
        direction="row"
        gap={1}
        flexWrap="wrap"
        role="group"
        aria-label={tr("Courbes affichées")}
        sx={{ mb: 2 }}
      >
        {available.map((key) => (
          <Button
            key={key}
            size="small"
            variant={active.includes(key) ? "outlined" : "text"}
            aria-pressed={active.includes(key)}
            onClick={() =>
              setSelected((prev) =>
                prev.includes(key)
                  ? prev.filter((k) => k !== key)
                  : [...prev, key]
              )
            }
            sx={{
              borderColor: active.includes(key) ? "divider" : "transparent",
              color: "text.primary",
              gap: 1,
            }}
          >
            <Box
              aria-hidden="true"
              sx={{
                width: 9,
                height: 9,
                borderRadius: "50%",
                bgcolor: metrics[key].color,
                opacity: active.includes(key) ? 1 : 0.3,
              }}
            />
            {tr(metrics[key].label)}
          </Button>
        ))}
      </Stack>
      {!rows.length ? (
        <Typography color="text.secondary" sx={{ py: 5 }}>
           {tr("Votre progression apparaîtra après la première évaluation.")} </Typography>
      ) : !active.length ? (
        <Typography role="status" color="text.secondary" sx={{ py: 5 }}>
           {tr("Sélectionnez au moins un critère pour afficher le graphique.")} </Typography>
      ) : (
        <Box
          sx={{ height: { xs: 260, sm: 290 }, minWidth: 0 }}
          role="img"
          aria-label={tr("Évolution des notes sur 10. Les valeurs exactes sont disponibles dans le tableau sous le graphique.")}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={rows}
              margin={{ top: 12, right: 14, left: -20, bottom: 8 }}
              accessibilityLayer
            >
              <CartesianGrid
                vertical={false}
                stroke={theme.palette.divider}
                strokeDasharray="3 5"
              />
              <XAxis
                dataKey="date"
                tickFormatter={(v) => dateText(v, "dd/MM")}
                axisLine={false}
                tickLine={false}
                minTickGap={26}
                tickMargin={12}
                tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
              />
              <YAxis
                domain={[0, 10]}
                ticks={[0, 2, 4, 6, 8, 10]}
                axisLine={false}
                tickLine={false}
                tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
              />
              <Tooltip
                labelFormatter={(v) => dateText(String(v))}
                formatter={(value: any, name: any) => [tr(scoreText(value, locale)), name]}
                contentStyle={{
                  background: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  border: "1px solid " + theme.palette.divider,
                  borderRadius: 12,
                  boxShadow: theme.shadows[4],
                }}
                itemStyle={{ color: theme.palette.text.primary }}
              />
              {active.map((key) => (
                <Line
                  key={key}
                  type="linear"
                  dataKey={key}
                  name={tr(metrics[key].label)}
                  stroke={metrics[key].color}
                  strokeWidth={key === "global_score" ? 3 : 2}
                  strokeDasharray={
                    key === "global_score" ? undefined : available.indexOf(key) % 2 === 0 ? "6 3" : "2 3"
                  }
                  dot={{
                    r: rows.length > 24 ? 2 : 4,
                    fill: theme.palette.background.paper,
                    strokeWidth: 2,
                  }}
                  activeDot={{ r: 6 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
      >
        {rows.length === 1
          ? tr("Une seule évaluation : une seconde séance permettra de voir une évolution.")
          : tr("Chaque point représente une séance évaluée. Survolez ou touchez un point pour lire les notes.")}
        {wellbeing
          ? tr(" « Niveau de forme » et « Gestion des émotions » : une note élevée indique une meilleure évaluation.")
          : tr(" Le score global enregistré sur 100 est affiché sur 10.")}
      </Typography>
      <Button
        size="small"
        sx={{ mt: 1 }}
        aria-expanded={table}
        aria-controls={id + "-values"}
        onClick={() => setTable((v) => !v)}
      >
        {table ? tr("Masquer les valeurs") : tr("Voir les valeurs exactes")}
      </Button>
      {table && (
        <TableContainer
          id={id + "-values"}
          sx={{ mt: 1, maxHeight: 320 }}
          tabIndex={0}
          role="region"
          aria-label={tr("Tableau des notes par séance")}
        >
          <Table size="small" stickyHeader>
            <caption>
               {tr("Notes sur 10 · Non renseigné : aucune valeur disponible.")} </caption>
            <TableHead>
              <TableRow>
                <TableCell>{tr("Date")}</TableCell>
                {active.map((key) => (
                  <TableCell key={key}>{tr(metrics[key].label)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={row.date + "-" + index}>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {dateText(row.date)}
                  </TableCell>
                  {active.map((key) => (
                    <TableCell key={key}>{tr(scoreText(row[key], locale))}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
